// Generic CRUD aggregate surface (PLAN-P3 U1c) — replaces the read-only AggregateList with
// list + create + inline edit + deactivate over the tenant-worker (RLS), via the api-client
// generic getEntity/updateEntity/deactivateEntity. Affordances are RBAC-gated by <Can> (UI
// affordance only; the worker enforces the verb server-side + RLS the rows). CRUD-CAPTURE-PROTOCOL.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "./api";
import { Can } from "./rbac";
import { useI18n } from "./i18n";
import { humanizeEnum } from "./enum-labels";

export interface CrudField { key: string; label: string; type?: "text" | "textarea" | "number" | "select"; required?: boolean; options?: string[]; }

// Columns the generic table must never surface (raw UUIDs + internal audit/scope fields). The row object
// still carries `id` (edit/deactivate read it); this only filters the DISPLAYED columns. (P3 UUID cleanup —
// fixes all AggregateCrud-backed registers at once.)
const HIDDEN_COLS = new Set(["id", "tenantId", "isActive", "deletedAt", "createdAt", "updatedAt", "createdBy", "updatedBy", "version", "contentSetId", "isLatest", "supersedesId", "source", "language"]);

function FieldInput({ f, value, onChange }: { f: CrudField; value: string; onChange: (v: string) => void }) {
  const { t } = useI18n();
  if (f.type === "textarea") return <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} style={{ ...input, fontFamily: "inherit" }} />;
  if (f.type === "select") return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={input}>
      <option value="">{t("crud.select")}</option>
      {f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return <input type={f.type === "number" ? "number" : "text"} value={value} onChange={(e) => onChange(e.target.value)} style={input} />;
}

export interface AggregateCrudProps {
  title: string;
  queryKey: string;
  basePath: string;
  permPrefix: string; // e.g. "bedrijfsmiddel" → write/update/delete
  listFn: () => Promise<unknown[]>;
  createFn?: (input: Record<string, unknown>) => Promise<unknown>;
  fields: CrudField[];
  empty: string;
  /** Shown instead of the create form when create is unavailable (e.g. needs a seeded library). */
  createBlockedNote?: string;
  /** Opt in to server-side search + pagination (X4b) via api.listPaged(basePath, …). Falls back to
   *  the client-side filter+slice over listFn when omitted. */
  serverPaged?: boolean;
  /** Optional extra per-row action(s) rendered in the actions cell (e.g. an Export button). */
  rowAction?: (row: Record<string, unknown>) => React.ReactNode;
  /** When set, rows become clickable to a detail page (design-vs-build drill-in). The actions cell
   *  stops propagation so edit/deactivate still work without triggering navigation. */
  onRowOpen?: (row: Record<string, unknown>) => void;
}

function errText(e: unknown): string {
  return e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message;
}
function buildInput(fields: CrudField[], state: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) { const v = state[f.key]?.trim(); if (v) out[f.key] = f.type === "number" ? Number(v) : v; }
  return out;
}
function missingRequired(fields: CrudField[], state: Record<string, string>): boolean {
  return fields.some((f) => f.required && !state[f.key]?.trim());
}

export function AggregateCrud(props: AggregateCrudProps) {
  const { title, queryKey, basePath, permPrefix, listFn, createFn, fields, empty, createBlockedNote, serverPaged, rowAction, onRowOpen } = props;
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: [queryKey] }); // prefix-matches the paged keys too
  const [editingId, setEditingId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const needle = q.trim().toLowerCase();

  // Server-side (X4b): api.listPaged → { rows, total }. Client-side (X4): load all, filter + slice.
  const serverQ = useQuery({
    queryKey: [queryKey, "paged", needle, page],
    queryFn: () => api.listPaged(basePath, { ...(needle ? { q: needle } : {}), limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    enabled: !!serverPaged,
  });
  const clientQ = useQuery({ queryKey: [queryKey], queryFn: listFn, enabled: !serverPaged });

  const isLoading = serverPaged ? serverQ.isLoading : clientQ.isLoading;
  const isFetching = serverPaged ? serverQ.isFetching : clientQ.isFetching;
  const error = serverPaged ? serverQ.error : clientQ.error;

  let rows: Array<Record<string, unknown>>; let total: number; let hasAny: boolean;
  if (serverPaged) {
    rows = (serverQ.data?.rows ?? []) as Array<Record<string, unknown>>;
    total = serverQ.data?.total ?? 0;
    hasAny = total > 0 || needle.length > 0; // keep the search box once a query has been typed
  } else {
    const allRows = (clientQ.data ?? []) as Array<Record<string, unknown>>;
    const filtered = needle ? allRows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(needle))) : allRows;
    total = filtered.length;
    const pages0 = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    rows = filtered.slice(Math.min(page, pages0 - 1) * PAGE_SIZE, Math.min(page, pages0 - 1) * PAGE_SIZE + PAGE_SIZE);
    hasAny = allRows.length > 0;
  }
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const columns = rows.length ? Object.keys(rows[0]!).filter((k) => !HIDDEN_COLS.has(k) && !/Id$/.test(k)).slice(0, 5) : [];

  return (
    <section>
      <h1 style={{ fontSize: 22 }}>{title} {isFetching && <span style={{ color: "#6b7280", fontSize: 13 }}>{t("crud.updating")}</span>}</h1>

      {createFn ? (
        <Can perm={`${permPrefix}.write`}>
          <CreateForm fields={fields} onCreate={createFn} onDone={invalidate} />
        </Can>
      ) : createBlockedNote ? (
        <p style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", fontSize: 13 }}>{createBlockedNote}</p>
      ) : null}

      {hasAny && (
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder={t("crud.search")} aria-label={t("crud.search")}
          style={{ display: "block", width: "100%", maxWidth: 360, padding: 8, margin: "8px 0", fontSize: 14, boxSizing: "border-box" }} />
      )}

      {isLoading && <p>{t("common.loading")}</p>}
      {error && <p style={{ color: "#b91c1c" }}>{t("crud.failed")} {errText(error)}</p>}
      {!isLoading && !error && total === 0 && !needle && <p style={{ color: "#6b7280" }}>{empty}</p>}
      {!isLoading && !error && total === 0 && needle && <p style={{ color: "#6b7280" }}>{t("crud.noMatches")} “{q}”.</p>}

      {rows.length > 0 && (
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
          <thead><tr>{columns.map((c) => <th key={c} style={th}>{c}</th>)}<th style={th}></th></tr></thead>
          <tbody>
            {rows.map((row, i) => {
              const id = row.id as string | undefined;
              return (
                <tr key={id ?? i}
                  style={onRowOpen && id ? { cursor: "pointer" } : undefined}
                  onClick={onRowOpen && id ? () => onRowOpen(row) : undefined}>
                  {columns.map((c) => <td key={c} style={td}>{format(row[c])}</td>)}
                  <td style={{ ...td, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                    {id && (
                      <>
                        <Can perm={`${permPrefix}.update`}>
                          <button style={link} onClick={() => setEditingId(editingId === id ? null : id)}>{editingId === id ? t("crud.close") : t("crud.edit")}</button>
                        </Can>
                        {rowAction?.(row)}
                        <Can perm={`${permPrefix}.delete`}>
                          <DeactivateButton basePath={basePath} id={id} onDone={invalidate} />
                        </Can>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, fontSize: 13 }}>
          <button type="button" style={pagerBtn} disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>{t("crud.prev")}</button>
          <span style={{ color: "#6b7280" }}>{t("crud.pageOf", safePage + 1, pages)} · {t("crud.countItems", total)}</span>
          <button type="button" style={pagerBtn} disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}>{t("crud.next")}</button>
        </div>
      )}

      {editingId && <EditPanel basePath={basePath} id={editingId} fields={fields} permPrefix={permPrefix} onDone={() => { invalidate(); }} onClose={() => setEditingId(null)} />}
    </section>
  );
}

function CreateForm({ fields, onCreate, onDone }: { fields: CrudField[]; onCreate: (i: Record<string, unknown>) => Promise<unknown>; onDone: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setState((s) => ({ ...s, [k]: v }));
  const mut = useMutation({ mutationFn: () => onCreate(buildInput(fields, state)), onSuccess: () => { setState({}); setOpen(false); onDone(); } });

  if (!open) return <button onClick={() => setOpen(true)} style={btnPrimary}>{t("crud.new")}</button>;
  return (
    <div style={card}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{t("crud.newItem")}</div>
      {fields.map((f) => (
        <label key={f.key} style={lbl}>{f.label}{f.required ? " *" : ""}
          <FieldInput f={f} value={state[f.key] ?? ""} onChange={(v) => set(f.key, v)} />
        </label>
      ))}
      {mut.isError && <p style={{ color: "#b91c1c", fontSize: 13 }}>{errText(mut.error)}</p>}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button onClick={() => mut.mutate()} disabled={mut.isPending || missingRequired(fields, state)} style={btnPrimary}>{mut.isPending ? t("crud.creating") : t("common.create")}</button>
        <button onClick={() => { setOpen(false); mut.reset(); }} style={btnGhost}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}

function EditPanel({ basePath, id, fields, permPrefix, onDone, onClose }: { basePath: string; id: string; fields: CrudField[]; permPrefix: string; onDone: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const { data, isLoading, error } = useQuery({ queryKey: [basePath, id], queryFn: () => api.getEntity(basePath, id) });
  const row = (data ?? null) as Record<string, unknown> | null;
  return (
    <div style={{ ...card, borderColor: "#bfdbfe" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 600 }}>{t("crud.edit")}</div>
        <button onClick={onClose} style={link}>{t("crud.close")}</button>
      </div>
      {isLoading && <p>{t("common.loading")}</p>}
      {error && <p style={{ color: "#b91c1c", fontSize: 13 }}>{errText(error)}</p>}
      {row && <EditForm basePath={basePath} id={id} row={row} fields={fields} permPrefix={permPrefix} onDone={onDone} />}
      <AuditTrail basePath={basePath} id={id} />
    </div>
  );
}

// X3 — generic change history from audit_log (create/update/soft_delete), newest first.
function AuditTrail({ basePath, id }: { basePath: string; id: string }) {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["audit", basePath, id], queryFn: () => api.listEntityAudit(basePath, id) });
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 16, borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t("crud.history")}</div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {rows.map((r) => (
          <li key={String(r.id)} style={{ fontSize: 12, color: "#6b7280", padding: "3px 0" }}>
            <span style={{ fontWeight: 600, color: "#374151" }}>{humanizeEnum(r.actionKind)}</span> · {r.occurredAt ? new Date(String(r.occurredAt)).toLocaleString() : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EditForm({ basePath, id, row, fields, permPrefix, onDone }: { basePath: string; id: string; row: Record<string, unknown>; fields: CrudField[]; permPrefix: string; onDone: () => void }) {
  const { t } = useI18n();
  const initial: Record<string, string> = {};
  for (const f of fields) initial[f.key] = row[f.key] != null ? String(row[f.key]) : "";
  const [state, setState] = useState<Record<string, string>>(initial);
  const set = (k: string, v: string) => setState((s) => ({ ...s, [k]: v }));
  const save = useMutation({ mutationFn: () => api.updateEntity(basePath, id, buildInput(fields, state)), onSuccess: onDone });

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 8 }}>id {id}</div>
      {fields.map((f) => (
        <label key={f.key} style={lbl}>{f.label}{f.required ? " *" : ""}
          <FieldInput f={f} value={state[f.key] ?? ""} onChange={(v) => set(f.key, v)} />
        </label>
      ))}
      {save.isError && <p style={{ color: "#b91c1c", fontSize: 13 }}>{errText(save.error)}</p>}
      {save.isSuccess && <p style={{ color: "#15803d", fontSize: 13 }}>{t("crud.saved")}</p>}
      <Can perm={`${permPrefix}.update`} fallback={<p style={{ color: "#6b7280", fontSize: 13 }}>{t("crud.readonly")}</p>}>
        <button onClick={() => save.mutate()} disabled={save.isPending || missingRequired(fields, state)} style={btnPrimary}>{save.isPending ? t("crud.saving") : t("crud.saveChanges")}</button>
      </Can>
    </div>
  );
}

function DeactivateButton({ basePath, id, onDone }: { basePath: string; id: string; onDone: () => void }) {
  const { t } = useI18n();
  const [confirm, setConfirm] = useState(false);
  const mut = useMutation({ mutationFn: () => api.deactivateEntity(basePath, id), onSuccess: () => { setConfirm(false); onDone(); } });
  if (!confirm) return <button style={{ ...link, color: "#b91c1c" }} onClick={() => setConfirm(true)}>{t("crud.deactivate")}</button>;
  return (
    <span style={{ fontSize: 13 }}>
      {t("crud.sure")} <button style={{ ...link, color: "#b91c1c" }} disabled={mut.isPending} onClick={() => mut.mutate()}>{mut.isPending ? "…" : t("crud.yes")}</button>
      <button style={link} onClick={() => setConfirm(false)}>{t("crud.no")}</button>
    </span>
  );
}

const th: React.CSSProperties = { textAlign: "left", borderBottom: "2px solid #e5e7eb", padding: "8px 10px", fontWeight: 600 };
const td: React.CSSProperties = { borderBottom: "1px solid #f3f4f6", padding: "8px 10px" };
const input: React.CSSProperties = { display: "block", width: "100%", padding: 8, marginTop: 4, boxSizing: "border-box", fontSize: 14, fontWeight: 400 };
const lbl: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, margin: "10px 0 2px" };
const card: React.CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, margin: "12px 0", background: "#fafafa" };
const btnPrimary: React.CSSProperties = { background: "#2563eb", color: "white", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 14, cursor: "pointer" };
const btnGhost: React.CSSProperties = { background: "transparent", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 14px", fontSize: 14, cursor: "pointer" };
const link: React.CSSProperties = { background: "transparent", border: "none", color: "#2563eb", cursor: "pointer", fontSize: 13, padding: "0 6px" };
const pagerBtn: React.CSSProperties = { background: "white", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 13, cursor: "pointer" };
function format(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
